import { FC, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@features/ui';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ContentTypeFormData } from '@src/types/knowledgeBase';

interface ContentTypeFormDialogProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialData?: Partial<ContentTypeFormData> | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ContentTypeFormData) => void;
}

const validationSchema = yup.object({
  key: yup
    .string()
    .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers and underscores')
    .required('Key is required'),
  name: yup.string().required('Name is required'),
  sort_order: yup
    .number()
    .transform((v, orig) => (orig === '' ? 0 : v))
    .min(0)
    .optional(),
  is_active: yup.boolean().optional(),
});

export const ContentTypeFormDialog: FC<ContentTypeFormDialogProps> = ({
  open,
  mode,
  initialData,
  onOpenChange,
  onSubmit,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ContentTypeFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: { key: '', name: '', sort_order: 0, is_active: true },
  });

  useEffect(() => {
    if (open) {
      reset(
        initialData
          ? { key: '', sort_order: 0, is_active: true, ...initialData }
          : { key: '', name: '', sort_order: 0, is_active: true }
      );
    }
  }, [open, initialData, reset]);

  const handleFormSubmit = (data: ContentTypeFormData) => {
    onSubmit(data);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[480px] max-w-[90vw] mx-auto">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-xl text-center">
            {mode === 'add' ? 'Add Type' : 'Edit Type'}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
          <Controller
            control={control}
            name="key"
            render={({ field }) => (
              <div className="space-y-1">
                <Label className="text-sm">
                  Key <span className="text-red-500">*</span>
                </Label>
                <Input
                  disabled={mode === 'edit'}
                  placeholder="e.g. no_refund_policy"
                  textSize="sm"
                  variant="secondary"
                  {...field}
                />
                {errors.key?.message && (
                  <p className="text-xs text-red-500 mt-0.5">{errors.key.message}</p>
                )}
              </div>
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <div className="space-y-1">
                <Label className="text-sm">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  placeholder="Display name"
                  textSize="sm"
                  variant="secondary"
                  {...field}
                />
                {errors.name?.message && (
                  <p className="text-xs text-red-500 mt-0.5">{errors.name.message}</p>
                )}
              </div>
            )}
          />
          <Controller
            control={control}
            name="sort_order"
            render={({ field }) => (
              <div className="space-y-1">
                <Label className="text-sm">Sort Order</Label>
                <Input
                  placeholder="0"
                  textSize="sm"
                  type="number"
                  variant="secondary"
                  {...field}
                  value={field.value ?? 0}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
                {errors.sort_order?.message && (
                  <p className="text-xs text-red-500 mt-0.5">{errors.sort_order.message}</p>
                )}
              </div>
            )}
          />
          <DialogFooter>
            <div className="flex items-center justify-center gap-6 pt-2">
              <Button size="sm" type="submit" variant="lime">
                {mode === 'add' ? 'Create' : 'Save'}
              </Button>
              <Button
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

